import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  LinearProgress,
} from "@material-ui/core";
import { Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon } from "@material-ui/icons";

import AddClassService from "./Locality/Service/addClassService";
import PushNotificationService from "./Locality/Service/pushNotificationService";
import PageTitle from "../../components/PageTitle/PageTitle";

export default function PushNotificationAdmin() {
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const schooleId = useMemo(() => {
    const id = userDetails?.schooleId || userDetails?.schoolId;
    return id && typeof id === "object" ? id?._id : id;
  }, [userDetails]);

  const [classesList, setClassesList] = useState([]);
  const [classIds, setClassIds] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [thanksAndRegards, setThanksAndRegards] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({
    percent: 0,
    message: "Preparing to send…",
  });
  const [status, setStatus] = useState(null);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editClassIds, setEditClassIds] = useState([]);
  const [editSubject, setEditSubject] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editThanks, setEditThanks] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const getClassNames = (ids) => {
    const idList = Array.isArray(ids) ? ids.map((x) => String(x)) : [];
    if (idList.length === 0) return "";
    const names = classesList
      .filter((c) => idList.includes(String(c._id)))
      .map((c) => c.className)
      .filter(Boolean);
    const missingCount = Math.max(0, idList.length - names.length);
    return `${names.join(", ")}${missingCount ? ` (+${missingCount})` : ""}`;
  };

  useEffect(() => {
    if (!schooleId) return;
    AddClassService.getAllAddClass(schooleId, "SCHOOLE")
      .then((res) => setClassesList(Array.isArray(res) ? res : res?.data || []))
      .catch(() => setClassesList([]));
  }, [schooleId]);

  const refreshAdminList = async () => {
    setLoadingList(true);
    try {
      const res = await PushNotificationService.listAdminNotifications();
      const data = res?.data || res;
      setAdminNotifications(Array.isArray(data?.notifications) ? data.notifications : []);
    } catch {
      setAdminNotifications([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (!schooleId) return;
    refreshAdminList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schooleId]);

  const canSend =
    classIds.length > 0 && subject.trim() && message.trim() && thanksAndRegards.trim();

  const onFilesChange = (event) => {
    const picked = Array.from(event.target.files || []);
    if (!picked.length) return;
    setFiles((prev) => [...prev, ...picked]);
    // allow selecting the same file again in a second pick
    event.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const onSend = async () => {
    if (!canSend || sending) return;
    setStatus(null);
    setSending(true);
    setSendProgress({
      percent: 0,
      message: files.length
        ? "Uploading files… Please wait."
        : "Sending notification… Please wait.",
    });
    try {
      const body = new FormData();
      classIds.forEach((id) => body.append("classIds", id));
      body.append("subject", subject.trim());
      body.append("message", message);
      body.append("thanksAndRegards", thanksAndRegards.trim());
      files.forEach((file) => body.append("files", file));

      const res = await PushNotificationService.sendNotification(body, (progress) => {
        setSendProgress({
          percent: progress.percent,
          message:
            files.length > 0
              ? `Uploading files… ${progress.percent}%`
              : "Sending notification… Please wait.",
        });
      });

      setSendProgress({ percent: 100, message: "Finishing…" });
      const data = res?.data || res;
      const successText = `Sent successfully. Push delivered to ${data?.pushCount ?? 0} device(s).`;
      setStatus({ type: "success", text: successText });
      alert(successText);
      setSubject("");
      setMessage("");
      setThanksAndRegards("");
      setClassIds([]);
      setFiles([]);
      refreshAdminList();
    } catch (e) {
      const errorText = e?.response?.data?.message || "Failed to send notification.";
      setStatus({ type: "error", text: errorText });
      alert(errorText);
    } finally {
      setSending(false);
      setSendProgress({ percent: 0, message: "Preparing to send…" });
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditClassIds((row?.classIds || []).map(String));
    setEditSubject(row?.subject || "");
    setEditMessage(row?.message || "");
    setEditThanks(row?.thanksAndRegards || "");
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditRow(null);
  };

  const saveEdit = async () => {
    if (!editRow?._id) return;
    setSavingEdit(true);
    try {
      await PushNotificationService.updateAdminNotification(editRow._id, {
        classIds: editClassIds,
        subject: editSubject,
        message: editMessage,
        thanksAndRegards: editThanks,
      });
      closeEdit();
      refreshAdminList();
    } catch (e) {
      setStatus({
        type: "error",
        text: e?.response?.data?.message || "Failed to update notification.",
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteRow = async (row) => {
    if (!row?._id) return;
    const ok = window.confirm("Delete this notification?");
    if (!ok) return;
    try {
      await PushNotificationService.deleteAdminNotification(row._id);
      refreshAdminList();
    } catch (e) {
      setStatus({
        type: "error",
        text: e?.response?.data?.message || "Failed to delete notification.",
      });
    }
  };

  return (
    <Container maxWidth="md">
      <PageTitle title=" Notification" />
      <Card style={{ padding: 16 }}>
        <Typography variant="h6" style={{ fontWeight: 700, color: "#0F3B62" }}>
          Send to Parents (by Class)
        </Typography>

        <Box mt={2}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="class-multi-label">Class (multi select)</InputLabel>
            <Select
              labelId="class-multi-label"
              multiple
              value={classIds}
              disabled={sending}
              onChange={(e) => setClassIds(e.target.value)}
              renderValue={(selected) => {
                const names = classesList
                  .filter((c) => selected.includes(c._id))
                  .map((c) => c.className);
                return names.join(", ");
              }}
              label="Class (multi select)"
            >
              {classesList.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  <Checkbox checked={classIds.indexOf(c._id) > -1} />
                  <ListItemText primary={c.className} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box mt={2}>
          <TextField
            label="Subject"
            variant="outlined"
            fullWidth
            value={subject}
            disabled={sending}
            onChange={(e) => setSubject(e.target.value)}
          />
        </Box>

        <Box mt={2}>
          <TextField
            label="Message"
            variant="outlined"
            fullWidth
            multiline
            minRows={6}
            value={message}
            disabled={sending}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message (unlimited spaces)."
          />
        </Box>

        <Box mt={2}>
          <TextField
            label="Thanks And Regards"
            variant="outlined"
            fullWidth
            value={thanksAndRegards}
            disabled={sending}
            onChange={(e) => setThanksAndRegards(e.target.value)}
          />
        </Box>

        <Box mt={2}>
          <input
            accept="image/*,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            id="push-notification-files"
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={onFilesChange}
          />
          <label htmlFor="push-notification-files">
            <Button
              variant="outlined"
              component="span"
              disabled={sending}
              style={{ borderColor: "#0F3B62", color: "#0F3B62" }}
            >
              {files.length > 0 ? "Add More Files" : "Upload Images / Excel"}
            </Button>
          </label>
          {files.length > 0 && (
            <Box mt={2}>
              <Typography variant="body2" style={{ color: "#475569", fontWeight: 700, marginBottom: 8 }}>
                Selected Files ({files.length})
              </Typography>
              <Grid container spacing={2}>
                {files.map((file, index) => (
                  <Grid item xs={6} sm={4} md={3} key={`${file.name}-${index}`}>
                    <Box
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        padding: 8,
                        background: "#f8fafc",
                        position: "relative",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => removeFile(index)}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "rgba(255,255,255,0.9)",
                          zIndex: 1,
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      {String(file.type || "").startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8 }}
                        />
                      ) : (
                        <Box
                          style={{
                            height: 120,
                            borderRadius: 8,
                            background: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px dashed #cbd5e1",
                            color: "#0F3B62",
                            fontWeight: 800,
                          }}
                        >
                          FILE
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        style={{ display: "block", marginTop: 6, color: "#64748b", wordBreak: "break-word" }}
                      >
                        {file.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>

        {status && (
          <Box mt={2}>
            <Typography
              variant="body2"
              style={{ color: status.type === "success" ? "#15803d" : "#b91c1c" }}
            >
              {status.text}
            </Typography>
          </Box>
        )}

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            disabled={!canSend || sending}
            onClick={onSend}
            style={{ backgroundColor: "#0F3B62" }}
          >
            {sending ? (
              <>
                <CircularProgress size={18} color="inherit" style={{ marginRight: 8 }} />
                Sending…
              </>
            ) : (
              "Send Notification"
            )}
          </Button>
        </Box>
      </Card>

      <Box mt={3}>
        <Card style={{ padding: 16 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
            <Typography variant="h6" style={{ fontWeight: 800, color: "#0F3B62" }}>
              Sent Notifications
            </Typography>
            <Button variant="outlined" onClick={refreshAdminList}>
              Refresh
            </Button>
          </Box>

          <Box mt={2} style={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Subject</b></TableCell>
                  <TableCell><b>Classes</b></TableCell>
                  <TableCell><b>Attachments</b></TableCell>
                  <TableCell><b>Created</b></TableCell>
                  <TableCell align="right"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingList ? (
                  <TableRow>
                    <TableCell colSpan={5}>Loading...</TableCell>
                  </TableRow>
                ) : adminNotifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>No notifications found.</TableCell>
                  </TableRow>
                ) : (
                  adminNotifications.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell style={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row.subject}
                      </TableCell>
                      <TableCell
                        style={{
                          maxWidth: 320,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={getClassNames(row.classIds)}
                      >
                        {getClassNames(row.classIds) || "-"}
                      </TableCell>
                      <TableCell>{Array.isArray(row.attachments) ? row.attachments.length : (Array.isArray(row.imageUrls) ? row.imageUrls.length : 0)}</TableCell>
                      <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleString() : ""}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(row)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => deleteRow(row)} title="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Card>
      </Box>

      <Dialog
        open={sending}
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ fontWeight: 700, color: "#0F3B62" }}>
          Sending Notification
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={2}
            px={1}
          >
            <CircularProgress size={48} style={{ color: "#0F3B62", marginBottom: 20 }} />
            <Typography variant="body1" align="center" style={{ fontWeight: 600 }}>
              {sendProgress.message}
            </Typography>
            <Box width="100%" mt={2.5}>
              <LinearProgress
                variant={
                  files.length > 0 && sendProgress.percent > 0
                    ? "determinate"
                    : "indeterminate"
                }
                value={sendProgress.percent}
                style={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              align="center"
              style={{ marginTop: 16 }}
            >
              Please wait until all files are uploaded and the notification is sent.
              Do not close this window.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Notification</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="edit-class-multi-label">Class (multi select)</InputLabel>
              <Select
                labelId="edit-class-multi-label"
                multiple
                value={editClassIds}
                onChange={(e) => setEditClassIds(e.target.value)}
                renderValue={(selected) => {
                  const names = classesList
                    .filter((c) => selected.includes(String(c._id)))
                    .map((c) => c.className);
                  return names.join(", ");
                }}
                label="Class (multi select)"
              >
                {classesList.map((c) => (
                  <MenuItem key={c._id} value={String(c._id)}>
                    <Checkbox checked={editClassIds.indexOf(String(c._id)) > -1} />
                    <ListItemText primary={c.className} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box mt={2}>
            <TextField label="Subject" variant="outlined" fullWidth value={editSubject} onChange={(e) => setEditSubject(e.target.value)} />
          </Box>
          <Box mt={2}>
            <TextField label="Message" variant="outlined" fullWidth multiline minRows={5} value={editMessage} onChange={(e) => setEditMessage(e.target.value)} />
          </Box>
          <Box mt={2}>
            <TextField label="Thanks And Regards" variant="outlined" fullWidth value={editThanks} onChange={(e) => setEditThanks(e.target.value)} />
          </Box>
          <Box mt={1}>
            <Typography variant="caption" style={{ color: "#64748b" }}>
              Note: attachments cannot be edited here (delete and re-send to change files).
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button color="primary" variant="contained" disabled={savingEdit} onClick={saveEdit} style={{ backgroundColor: "#0F3B62" }}>
            {savingEdit ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

